import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { WeekProfileResponse } from "../pages/schedule-misalignment-route/schedule-misalignment.types"
import { useWindowQuery } from "./use-window-query"
import type { WindowQuery } from "./use-window-query"

// The E2 week leaf (`.../{site_id}/{enduse}:{YYYY-Www}:profile`). The `:profile`
// suffix distinguishes it from the bare week key, which the Sankey already uses
// for that week's day column. Same `Any`-typed OpenAPI situation as the day
// profile, so it goes through the shared axios client rather than the generated
// typed one.
const getWeekProfile = async (
  organizationId: number,
  siteId: string,
  enduse: string,
  weekKey: string,
  win: WindowQuery
): Promise<WeekProfileResponse> => {
  try {
    const res = await httpClient.get<WeekProfileResponse>(
      `/api/v1/neat/insights/schedule-misalignment/${siteId}/${enduse}:${weekKey}:profile`,
      { params: { organization_id: organizationId, ...win } }
    )
    return res.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Lazily resolves the week profile for `(siteId, enduse, weekKey)` — the
 * expected-vs-actual chart shown when a week card is selected in the Sankey.
 * No fetch until all three are set.
 */
export const useWeekProfile = (
  siteId: string | null,
  enduse: string | null,
  weekKey: string | null
) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const enabled =
    siteId != null &&
    enduse != null &&
    weekKey != null &&
    activeOrganizationId != null
  const queryKey = useQueryKey(
    ApiQueryName.WeekProfile,
    "getOne",
    siteId != null && enduse != null && weekKey != null
      ? `week:${siteId}:${enduse}:${weekKey}`
      : null,
    win
  )
  const queryResult = useQuery<WeekProfileResponse>({
    queryKey,
    enabled,
    queryFn: () =>
      getWeekProfile(
        Number(activeOrganizationId),
        siteId as string,
        enduse as string,
        weekKey as string,
        win
      ),
  })
  return useRenameQueryResult(queryResult, ApiQueryName.WeekProfile)
}
